// src/components/NotificationCenter.js
//
// Phase 4 — Bell icon + notification centre.
// -------------------------------------------
// Subscribes to the Firestore `notifications` collection filtered by
// the current user's uid, shows an unread badge, and lets the user
// mark items as read (individually or all at once).

import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  Typography,
  Tooltip
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  notificationService,
  countUnread
} from '../services/notificationService';

const formatRelative = (ts, locale) => {
  if (!ts) return '';
  try {
    const date =
      typeof ts.toDate === 'function'
        ? ts.toDate()
        : ts instanceof Date
          ? ts
          : new Date(ts);
    if (Number.isNaN(date.getTime())) return '';
    const diffMs = Date.now() - date.getTime();
    const diffMin = Math.round(diffMs / 60000);
    if (diffMin < 1) return locale === 'ar' ? 'الآن' : 'just now';
    if (diffMin < 60) {
      return locale === 'ar' ? `منذ ${diffMin} د` : `${diffMin}m ago`;
    }
    const diffH = Math.round(diffMin / 60);
    if (diffH < 24) {
      return locale === 'ar' ? `منذ ${diffH} س` : `${diffH}h ago`;
    }
    const diffD = Math.round(diffH / 24);
    return locale === 'ar' ? `منذ ${diffD} ي` : `${diffD}d ago`;
  } catch (_) {
    return '';
  }
};

const NotificationCenter = () => {
  const { t, i18n } = useTranslation();
  const { currentUser } = useAuth();
  const [items, setItems] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    if (!currentUser?.uid) {
      setItems([]);
      return undefined;
    }
    const unsubscribe = notificationService.subscribeForUser(
      currentUser.uid,
      (next) => setItems(next || []),
      { max: 20 }
    );
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [currentUser?.uid]);

  const unreadCount = useMemo(() => countUnread(items), [items]);

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleMarkAllRead = async () => {
    if (!currentUser?.uid) return;
    await notificationService.markAllAsRead(currentUser.uid);
  };

  const handleItemClick = async (item) => {
    if (!item.read) {
      await notificationService.markAsRead(item.id);
    }
  };

  const emptyLabel = t('notifications.empty');
  const titleLabel = t('notifications.title');
  const markAllLabel = t('notifications.markAllRead');

  return (
    <>
      <Tooltip title={titleLabel}>
        <IconButton
          onClick={handleOpen}
          color="inherit"
          aria-label={titleLabel}
          size="large"
        >
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 340, maxHeight: 480 } } }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            {titleLabel}
          </Typography>
          <Button
            size="small"
            onClick={handleMarkAllRead}
            disabled={unreadCount === 0}
          >
            {markAllLabel}
          </Button>
        </Box>
        <Divider />
        {items.length === 0 ? (
          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {emptyLabel}
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {items.map((item) => (
              <ListItem key={item.id} disablePadding>
                <ListItemButton
                  onClick={() => handleItemClick(item)}
                  sx={{
                    backgroundColor: item.read
                      ? 'transparent'
                      : 'action.hover',
                    alignItems: 'flex-start'
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        fontWeight={item.read ? 'normal' : 'bold'}
                      >
                        {item.title || item.body || t('notifications.defaultTitle')}
                      </Typography>
                    }
                    secondary={
                      <>
                        {item.title && item.body ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {item.body}
                          </Typography>
                        ) : null}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          {formatRelative(item.createdAt, i18n.language)}
                        </Typography>
                      </>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Menu>
    </>
  );
};

export default NotificationCenter;
